import React, { useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import { Alert, Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { PlanId, PlanLimits, PLAN_LIMITS } from "../../api/organizations";
import {
  PlanCatalogItem,
  fetchPlanCatalog,
  updatePlanCatalogItem,
} from "../../api/planCatalog";
import "./intelligence/IntelligenceGlass.css";

const PLAN_IDS: PlanId[] = ["micro", "standard", "professional", "enterprise"];

interface Props {
  show: boolean;
  user: User;
  onHide: () => void;
  onCatalogUpdated: (plans: Record<PlanId, PlanLimits>) => void;
}

const toPlanMap = (items: PlanCatalogItem[]): Record<PlanId, PlanLimits> => {
  const map = { ...PLAN_LIMITS };
  for (const item of items) {
    if (PLAN_IDS.includes(item.id)) {
      map[item.id] = item;
    }
  }
  return map;
};

export const GlobalPlanCatalogModal: React.FC<Props> = ({
  show,
  user,
  onHide,
  onCatalogUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<Record<PlanId, PlanCatalogItem>>(
    PLAN_IDS.reduce(
      (acc, id) => ({ ...acc, [id]: { ...(PLAN_LIMITS[id] as PlanCatalogItem), id } }),
      {} as Record<PlanId, PlanCatalogItem>
    )
  );
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId>("micro");
  const [syncStripe, setSyncStripe] = useState(true);
  const [monthlyPriceInput, setMonthlyPriceInput] = useState("");

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    setError(null);
    fetchPlanCatalog(user)
      .then((items) => {
        const nextMap = PLAN_IDS.reduce(
          (acc, id) => ({ ...acc, [id]: { ...(PLAN_LIMITS[id] as PlanCatalogItem), id } }),
          {} as Record<PlanId, PlanCatalogItem>
        );
        for (const item of items) {
          if (PLAN_IDS.includes(item.id)) nextMap[item.id] = item;
        }
        setPlans(nextMap);
        onCatalogUpdated(toPlanMap(items));
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load plans"))
      .finally(() => setLoading(false));
  }, [show, user, onCatalogUpdated]);

  const selected = useMemo(() => plans[selectedPlanId], [plans, selectedPlanId]);
  const marketingFeaturesText = (selected.marketingFeatures || []).join("\n");

  useEffect(() => {
    const value =
      typeof selected?.monthlyPrice === "number" && Number.isFinite(selected.monthlyPrice)
        ? String(selected.monthlyPrice)
        : "";
    setMonthlyPriceInput(value);
  }, [selectedPlanId, selected?.monthlyPrice]);

  const patchSelected = (patch: Partial<PlanCatalogItem>) => {
    setPlans((prev) => ({
      ...prev,
      [selectedPlanId]: {
        ...prev[selectedPlanId],
        ...patch,
      },
    }));
  };

  const saveSelected = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = plans[selectedPlanId];
      const parsedMonthlyPrice =
        payload.billingType === "quote"
          ? null
          : Number(monthlyPriceInput.replace(",", "."));
      const fixedMonthlyPrice = parsedMonthlyPrice ?? NaN;
      if (
        payload.billingType !== "quote" &&
        (!Number.isFinite(fixedMonthlyPrice) || fixedMonthlyPrice < 0)
      ) {
        throw new Error("Monthly price must be a valid non-negative decimal number.");
      }
      const updated = await updatePlanCatalogItem(user, selectedPlanId, {
        name: payload.name,
        monthlyPrice: parsedMonthlyPrice,
        currency: payload.currency || "USD",
        billingType: payload.billingType || "fixed",
        maxSalesPeople: Number(payload.maxSalesPeople || 0),
        maxCustomers: Number(payload.maxCustomers || 0),
        maxInventoryItems: Number(payload.maxInventoryItems || 0),
        maxSuppliers: Number(payload.maxSuppliers || 0),
        maxAdmins: Number(payload.maxAdmins || 0),
        marketingFeatures: (payload.marketingFeatures || []).filter(Boolean),
        highlighted: !!payload.highlighted,
        stripeProductId: payload.stripeProductId || null,
        syncStripe,
        intelligenceCapacityId: payload.intelligenceCapacityId || null,
        intelligenceDiscountPercent:
          payload.intelligenceDiscountPercent ?? null,
      });
      setPlans((prev) => ({ ...prev, [selectedPlanId]: updated }));
      onCatalogUpdated(toPlanMap(Object.values({ ...plans, [selectedPlanId]: updated })));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => !saving && onHide()} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Global plan catalog</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            <span>Loading plans...</span>
          </div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Plan</Form.Label>
              <Form.Select
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value as PlanId)}
              >
                {PLAN_IDS.map((p) => (
                  <option key={p} value={p}>
                    {plans[p]?.name || p}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Display name</Form.Label>
              <Form.Control
                value={selected.name}
                onChange={(e) => patchSelected({ name: e.target.value })}
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Billing type</Form.Label>
                  <Form.Select
                    value={selected.billingType || "fixed"}
                    onChange={(e) =>
                      patchSelected({ billingType: e.target.value as "fixed" | "quote" })
                    }
                  >
                    <option value="fixed">Fixed</option>
                    <option value="quote">Quote</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Control
                    value={selected.currency || "USD"}
                    onChange={(e) =>
                      patchSelected({ currency: e.target.value.toUpperCase() })
                    }
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Monthly price</Form.Label>
              <InputGroup>
                <InputGroup.Text>{selected.currency || "USD"}</InputGroup.Text>
                <Form.Control
                  value={monthlyPriceInput}
                  disabled={selected.billingType === "quote"}
                  onChange={(e) => setMonthlyPriceInput(e.target.value)}
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Marketing features (one per line)</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={marketingFeaturesText}
                onChange={(e) =>
                  patchSelected({
                    marketingFeatures: e.target.value
                      .split("\n")
                      .map((line) => line.trim())
                      .filter(Boolean),
                  })
                }
              />
            </Form.Group>

            <div className="intel-glass-panel mb-3">
              <h6 className="mb-2">Intelligence bundle offer</h6>
              <p className="intel-glass-hint">
                Optional discount when this base plan is selected (capacity is
                still an independent add-on).
              </p>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3 mb-md-0">
                    <Form.Label>Capacity</Form.Label>
                    <Form.Select
                      value={selected.intelligenceCapacityId || ""}
                      onChange={(e) =>
                        patchSelected({
                          intelligenceCapacityId: (e.target.value || null) as
                            | "lite"
                            | "plus"
                            | "extra"
                            | null,
                        })
                      }
                    >
                      <option value="">None</option>
                      <option value="lite">Lite</option>
                      <option value="plus">Plus</option>
                      <option value="extra">Extra</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-0">
                    <Form.Label>Discount %</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      max={100}
                      value={selected.intelligenceDiscountPercent ?? ""}
                      onChange={(e) =>
                        patchSelected({
                          intelligenceDiscountPercent: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                    />
                  </Form.Group>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Max sales people</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={selected.maxSalesPeople}
                    onChange={(e) =>
                      patchSelected({ maxSalesPeople: Number(e.target.value || 0) })
                    }
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Max customers</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={selected.maxCustomers}
                    onChange={(e) =>
                      patchSelected({ maxCustomers: Number(e.target.value || 0) })
                    }
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Max inventory items</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={selected.maxInventoryItems}
                    onChange={(e) =>
                      patchSelected({ maxInventoryItems: Number(e.target.value || 0) })
                    }
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Max suppliers</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={selected.maxSuppliers ?? 0}
                    onChange={(e) =>
                      patchSelected({ maxSuppliers: Number(e.target.value || 0) })
                    }
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Max admins</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={selected.maxAdmins}
                    onChange={(e) =>
                      patchSelected({ maxAdmins: Number(e.target.value || 0) })
                    }
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Stripe product ID (optional)</Form.Label>
              <Form.Control
                value={selected.stripeProductId || ""}
                onChange={(e) => patchSelected({ stripeProductId: e.target.value })}
                placeholder="prod_..."
              />
              <Form.Text muted>
                Latest Stripe price: {selected.stripePriceId || "not synced yet"}
              </Form.Text>
            </Form.Group>
            <Form.Check
              type="switch"
              id="sync-stripe-switch"
              className="mb-2"
              checked={syncStripe}
              onChange={(e) => setSyncStripe(e.target.checked)}
              label="Sync this update to Stripe"
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Close
        </Button>
        <Button variant="primary" onClick={saveSelected} disabled={saving || loading}>
          {saving ? "Saving..." : "Save plan"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
